import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, ArrowLeft, ArrowRight } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_koko-showcase/artifacts/qadunrd3_1000069989-removebg-preview%20%281%29.png";

export default function AdminLogin({ portal = "admin" }) {
  const isStaff = portal === "staff";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [alreadyLoggedIn, setAlreadyLoggedIn] = useState(false);
  const [existingUser, setExistingUser] = useState(null);
  const navigate = useNavigate();

  // If already signed in, show Dashboard CTA instead of login form
  useEffect(() => {
    const token = localStorage.getItem("koko_token");
    const stored = localStorage.getItem("koko_user");
    if (!token) { setCheckingSession(false); return; }
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        setExistingUser(data || (stored ? JSON.parse(stored) : null));
        setAlreadyLoggedIn(true);
      })
      .catch(() => {
        // stale token → clear and show login form
        localStorage.removeItem("koko_token");
        localStorage.removeItem("koko_user");
        localStorage.removeItem("koko_refresh_token");
      })
      .finally(() => setCheckingSession(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("koko_token", data.access_token);
      localStorage.setItem("koko_user", JSON.stringify({ name: data.name, email: data.email, role: data.role }));
      if (remember && data.refresh_token) {
        localStorage.setItem("koko_refresh_token", data.refresh_token);
      } else {
        localStorage.removeItem("koko_refresh_token");
      }
      // Route by role — admins always go to /admin, everyone else to /staff
      navigate(data.role === "admin" ? "/admin" : "/staff");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join(" ") : "Login failed. Please try again.");
    }
    setLoading(false);
  };

  if (checkingSession) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-xs uppercase tracking-widest text-zinc-500">Checking session…</div>
      </div>
    );
  }

  return (
    <div data-testid={isStaff ? "staff-login-page" : "admin-login-page"} className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4">
      <Helmet>
        <title>{`${isStaff ? "Staff Login" : "Admin Login"} - KoKo Sites`}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-6">
            <img src={LOGO_URL} alt="KoKo Sites" className="h-14 mx-auto" />
          </Link>
          <h1 className="text-2xl font-black uppercase tracking-tight text-white mb-2">{isStaff ? "Staff Portal" : "Admin Panel"}</h1>
          <p className="text-sm text-zinc-500">
            {alreadyLoggedIn ? "You're already signed in" : (isStaff ? "Sign in to your workspace" : "Sign in to manage reviews and messages")}
          </p>
        </div>

        <div className="border border-white/10 bg-[#141414] p-8">
          {alreadyLoggedIn ? (
            <div className="space-y-5 text-center">
              <div className="bg-green-500/10 border border-green-500/30 px-4 py-3">
                <p className="text-[10px] font-bold uppercase tracking-widest text-green-400">Signed in as</p>
                <p className="text-sm text-white font-bold mt-1 break-all">{existingUser?.email || existingUser?.name || "Admin"}</p>
              </div>
              <button
                onClick={() => navigate(existingUser?.role === "admin" ? "/admin" : "/staff")}
                data-testid="go-to-dashboard-btn"
                className="w-full bg-red-600 text-white font-bold uppercase tracking-widest py-4 px-8 border border-transparent hover:bg-white hover:text-black transition-colors duration-200 text-sm flex items-center justify-center gap-2"
              >
                Go to Dashboard <ArrowRight size={14} />
              </button>
              <button
                onClick={() => {
                  localStorage.removeItem("koko_token");
                  localStorage.removeItem("koko_user");
                  localStorage.removeItem("koko_refresh_token");
                  setAlreadyLoggedIn(false);
                  setExistingUser(null);
                }}
                data-testid="switch-account-btn"
                className="w-full text-xs text-zinc-500 hover:text-red-500 transition-colors uppercase tracking-wider py-2"
              >
                Switch account / Sign out
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="email" className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Email</Label>
                <Input
                  id="email" type="email" inputMode="email" autoComplete="email" enterKeyHint="next"
                  data-testid="admin-login-email" value={email}
                  onChange={e => setEmail(e.target.value)} required placeholder={isStaff ? "you@kokosites.com" : "admin@kokosites.com"}
                  className="bg-transparent border-b-2 border-white/20 rounded-none px-0 py-3 text-white focus:border-red-600 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Password</Label>
                <Input
                  id="password" type="password" autoComplete="current-password" enterKeyHint="go"
                  data-testid="admin-login-password" value={password}
                  onChange={e => setPassword(e.target.value)} required placeholder="Enter password"
                  className="bg-transparent border-b-2 border-white/20 rounded-none px-0 py-3 text-white focus:border-red-600 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              </div>

              <label className="flex items-center gap-3 text-xs text-zinc-400 cursor-pointer select-none group">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={e => setRemember(e.target.checked)}
                  data-testid="admin-login-remember"
                  className="w-4 h-4 accent-red-600 cursor-pointer"
                />
                <span className="uppercase tracking-wider group-hover:text-white transition-colors">Keep me signed in</span>
              </label>

              {error && (
                <div data-testid="admin-login-error" className="bg-red-600/10 text-red-400 border border-red-600/20 p-3 text-sm">
                  {error}
                </div>
              )}

              <button type="submit" disabled={loading} data-testid="admin-login-submit"
                className="w-full bg-red-600 text-white font-bold uppercase tracking-widest py-4 px-8 border border-transparent hover:bg-white hover:text-black transition-colors duration-200 text-sm disabled:opacity-50 flex items-center justify-center gap-2">
                <Lock size={14} /> {loading ? "Signing In..." : "Sign In"}
              </button>
            </form>
          )}
        </div>

        <div className="text-center mt-6">
          <Link to="/" className="text-xs text-zinc-500 hover:text-red-500 transition-colors uppercase tracking-wider inline-flex items-center gap-2">
            <ArrowLeft size={12} /> Back to Website
          </Link>
        </div>
      </div>
    </div>
  );
}
